import type { Column } from "@shared/types/data-table"
import type { Quote } from "@shared/types/quotes"
import type { ColumnManagementConfig } from "@shared/lib/tables/utils/column-management"
import {
  createDateColumn,
} from "./column-factories"
import { formatDateOnly } from '@shared/utils/date'
import { CurrencyCell } from "../cells/common-cells"
import { AppStatusBadge } from "@shared/components/ui/status-badge/AppStatusBadge"

/**
 * Custom Amount Column
 * Displays amount only
 */
function createAmountColumn(): Column<Quote> {
  return {
    key: "amount",
    header: "Amount",
    width: 120,
    render: (item: Quote) => (
      <CurrencyCell
        amount={item.amount}
        currency={item.currency}
        align="left"
        className="text-sm min-w-[68px]"
      />
    )
  }
}

/**
 * Custom Status Column without header
 * Compact column just for status badge
 */
function createStatusOnlyColumn(): Column<Quote> {
  return {
    key: "status",
    header: "",
    width: 100,
    render: (item: Quote) => (
      <AppStatusBadge entityType="quote" status={item.status} />
    )
  }
}

/**
 * Custom Quote Number Column
 * Displays "QT-DRAFT" in color #8FA5A9 for draft quotes
 * Displays "QT-XXXXXXXX" for other statuses
 */
function createQuoteNumberColumn(): Column<Quote> {
  return {
    key: "quoteNumber",
    header: "Quote #",
    width: 150,
    render: (item: Quote) => {
      const isDraft = item.status === 'draft'
      return (
        <span
          className={`font-mono text-sm ${isDraft ? 'text-muted-foreground' : ''}`}
        >
          {item.quoteNumber}
        </span>
      )
    }
  }
}

/**
 * Custom Customer Column
 * Displays customer name only
 */
function createCustomerColumn(): Column<Quote> {
  return {
    key: "customer",
    header: "Customer",
    width: 160,
    render: (item: Quote) => (
      <span className="text-sm font-normal text-foreground">
        {item.customer}
      </span>
    )
  }
}

/**
 * Custom Customer Email Column
 * Displays customer email in muted color
 */
function createCustomerEmailColumn(): Column<Quote> {
  return {
    key: "customerEmail",
    header: "Email",
    width: 200,
    render: (item: Quote) => (
      <span className="text-sm text-muted-foreground">
        {item.customerEmail}
      </span>
    )
  }
}

/**
 * Custom Send Date Column
 * Displays date or "-" if not sent
 */
function createSendColumn(): Column<Quote> {
  return {
    key: "send",
    header: "Send",
    width: 120,
    render: (item: Quote) => {
      if (!item.send) {
        return <span className="text-muted-foreground">−</span>
      }
      return (
        <span className="text-sm text-muted-foreground group-hover:text-foreground">
          {formatDateOnly(item.send)}
        </span>
      )
    }
  }
}

/**
 * Custom Expiration Date Column
 * Displays date or "-" if no expiration
 */
function createExpirationColumn(): Column<Quote> {
  return {
    key: "expiration",
    header: "Expiration",
    width: 120,
    render: (item: Quote) => {
      if (!item.expiration) {
        return <span className="text-muted-foreground">−</span>
      }
      return (
        <span className="text-sm text-muted-foreground group-hover:text-foreground">
          {formatDateOnly(item.expiration)}
        </span>
      )
    }
  }
}

export const QUOTE_COLUMNS: Record<string, Column<Quote>> = {
  amount: createAmountColumn(),
  status: createStatusOnlyColumn(),
  quoteNumber: createQuoteNumberColumn(),
  customer: createCustomerColumn(),
  customerEmail: createCustomerEmailColumn(),
  expiration: createExpirationColumn(),
  send: createSendColumn(),
  created: createDateColumn<Quote>("created", "Created", 120, false),
}

export function getQuoteColumns(): Column<Quote>[] {
  return [
    QUOTE_COLUMNS.amount,
    QUOTE_COLUMNS.status,
    QUOTE_COLUMNS.quoteNumber,
    QUOTE_COLUMNS.customer,
    QUOTE_COLUMNS.customerEmail,
    QUOTE_COLUMNS.expiration,
    QUOTE_COLUMNS.send,
    QUOTE_COLUMNS.created,
  ]
}

export function getQuoteColumnConfig(): ColumnManagementConfig[] {
  const allColumns = Object.entries(QUOTE_COLUMNS).map(([key, column]) => ({
    id: key,
    label: typeof column.header === 'string' ? column.header : key,
    visible: [
      'amount',
      'status',
      'quoteNumber',
      'customer',
      'customerEmail',
      'expiration',
      'send',
      'created'
    ].includes(key),
  }))

  return allColumns
}
